'use client'

import * as React from 'react'
import Link from 'next/link'
import posthog from 'posthog-js'
import { useEffect } from 'react'
import { Spinner } from '@/components/ui/spinner'
import { useChunkErrorRecovery } from '@/hooks/use-chunk-error-recovery'
import { CHUNK_ERROR_COPY, isChunkLoadError } from '@/lib/chunk-error'

/**
 * Root error boundary (#308).
 *
 * Before this file existed the only route boundaries were inside (app), so a
 * client-side error anywhere in the unauthenticated funnel - (auth) /login,
 * /signup, /forgot-password, /reset-password, plus (marketing),
 * (book-editor) and /admin - fell all the way through to global-error.tsx,
 * which renders `NextError statusCode={0}`: the bare, unbranded Next.js page
 * reading "Application error: a client-side exception has occurred", with no
 * explanation and no way back. A root boundary catches those first.
 *
 * Placed at the root rather than in (auth) deliberately. The gap is not
 * specific to login: every group except (app) had the same hole, and a new
 * route group would silently inherit it. One boundary here closes the class.
 * (app) keeps its own boundary so authed errors still render inside the app
 * chrome instead of replacing it.
 *
 * global-error.tsx stays as the last resort for errors thrown by the root
 * layout itself, which no boundary below it can catch. It is also why this
 * file reports to PostHog: global-error.tsx was the capture site for every
 * error in the unauthenticated funnel, so catching them here without
 * reporting would silently take them off the error-tracking dashboard.
 */
export default function RootError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const reloading = useChunkErrorRecovery(error)
  const isChunkError = isChunkLoadError(error)

  useEffect(() => {
    // PostHog's window.onerror auto-capture does not see errors a React
    // boundary caught, so this explicit call is what puts them on the
    // dashboard. PostHog dedupes by stack hash, so any overlap is harmless.
    posthog.captureException(error)
  }, [error])

  // One automatic reload is in flight. Rendering the full error page here
  // would flash an alarming screen the visitor never needed to read.
  if (reloading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <Spinner label="Reloading" className="mb-6" />
          <p className="font-serif text-text-secondary leading-relaxed">
            Reloading&hellip;
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6">
      <div className="max-w-md text-center" role="alert">
        <p
          lang="zh-Hant"
          translate="no"
          className="font-serif-zh text-4xl md:text-5xl text-gold opacity-85 mb-6"
        >
          {isChunkError ? '斷' : '錯'}
        </p>
        <p
          lang="zh-Hant"
          translate="no"
          className="font-serif-zh text-sm font-light tracking-widest text-gold mb-2"
        >
          {isChunkError ? '連線中斷' : '出了問題'}
        </p>
        <h1 className="font-serif text-2xl text-ink mb-3">
          {/* These are JS strings, not JSX text, so the apostrophe is a real
              character rather than `&apos;` - an entity would render
              literally here. */}
          {isChunkError ? CHUNK_ERROR_COPY.title : 'Something went wrong'}
        </h1>
        <p className="font-serif text-text-secondary leading-relaxed mb-8">
          {isChunkError
            ? CHUNK_ERROR_COPY.description
            : 'It’s not your fault. Try again in a moment, and if it keeps happening, email support@baohua.app.'}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          {isChunkError ? (
            // Same reasoning as the automatic path in useChunkErrorRecovery:
            // reset() re-renders into the same document, whose webpack
            // registry is still missing the chunk. Only a document reload
            // re-fetches the HTML and its asset manifest.
            <button
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              {CHUNK_ERROR_COPY.retryLabel}
            </button>
          ) : (
            <button onClick={reset} className="btn-primary">
              Try again
            </button>
          )}
          <Link href="/" className="btn-secondary inline-block">
            Back home
          </Link>
        </div>
      </div>
    </div>
  )
}
